import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";

import {
    GC_ORDER_STATUS_DELAYED_NOTICE_TIMEOUT,
    GC_ORDER_STATUS_POLLING_INTERVAL,
    INITIAL_GC_ORDER_STATUS_POLL_DELAY,
} from "@/shared/constants";
import {
    SDK_STATUSES,
    CHECKOUT_FIELDS,
    POLLING_STATUS_CODES,
    ORDER_STATUSES,
    PROMOTION_ERROR_CODE,
    SUCCESS_STATUS_CODES,
} from "@/shared/gift-cards.constants";
import { getParam } from "@/shared/helpers/url-helper";
import { postToApp, WEBVIEW_MESSAGE_TYPES } from "@/shared/webview-bridge.constants";

import { processOrder } from "@/utils/afterpay-checkout";

import {
    getOrderStatus,
    isPromotionFailure,
    OrderFulfillmentError,
} from "@/pages/api/store-directory/gift-cards";

import { useErrorMessage } from "@/context/ErrorMessageContext";
import { useCurrentLocale } from "@/context/LanguageContext";

import { ACCENT_COLORS } from "@/components/component.constants";
import SplitterNumbers from "@/components/splitters/splitter-numbers/splitter-numbers";

import {
    Consumer,
    GiftCardCheckout,
    GiftCardProps,
    OrderStatus,
    Recipient,
} from "@/interfaces/gift-cards/gift-card-interface";
import { transformCheckout } from "@/interfaces/gift-cards/gift-card-transform";

import AlertCircleIconArcade from "../../assets/icons/alert-circle-arcade.svg";
import { GiftCardCheckoutNotification } from "./gift-card-checkout-notification";
import GiftCardDetailsLayout from "./gift-card-details-layout";
import { GiftCardOrderDelayed } from "./gift-card-order-delayed";
import { GiftCardPromoFailed } from "./gift-card-promo-failed";
import { InAppNotificationShell } from "./in-app-notification-shell";
import PaymentConfirmationLayout from "./payment-confirmation-layout";

const initialConsumer = {
    firstname: "",
    lastname: "",
    phone: "",
    email: "",
    confirmEmail: "",
};
const initialRecipient = {
    name: "",
    phone: "",
    email: "",
    confirmEmail: "",
    message: "",
};

type Props = {
    giftCard: GiftCardProps;
};
const GiftCardLayout: React.FC<Props> = ({ giftCard }) => {
    const { t } = useTranslation();
    const sectionOne = {
        headline: t("giftCards.howItWorks.sectionOne.headline"),
        body: "",
    };
    const sectionTwo = {
        headline: t("giftCards.howItWorks.sectionTwo.headline"),
        body: "",
    };
    const sectionThree = {
        headline:
            giftCard.supplier === "NBI"
                ? t("giftCards.howItWorks.sectionThree.headlineNBI")
                : t("giftCards.howItWorks.sectionThree.headline"),
        body: "",
    };

    const howItWorksSections = [sectionOne, sectionTwo, sectionThree];
    const [consumer, setConsumer] = useState<Consumer>(initialConsumer);
    const [recipient, setRecipient] = useState<Recipient>(initialRecipient);
    const [orderStatus, setOrderStatus] = useState<OrderStatus>(null);
    const [orderId, setOrderId] = useState<number>(null);
    const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
    const [paramStatus, setParamStatus] = useState<string>("");
    const [paramToken, setParamToken] = useState<string>("");
    const [isPurchaseFlowComplete, setIsPurchaseFlowComplete] = useState<boolean>(false);
    const { createErrorMessage } = useErrorMessage();
    const [showErrorOverlay, setShowErrorOverlay] = useState<boolean>(false);
    const [showDelayedOverlay, setShowDelayedOverlay] = useState<boolean>(false);
    // Set (to the PROMOTION_* error_code) when the order was cancelled because its
    // promotional offer could not be applied; drives the in-app "offer failed" notice.
    const [promoFailedReason, setPromoFailedReason] = useState<PROMOTION_ERROR_CODE | null>(null);

    const router = useRouter();
    const shouldSkipLogin = router.query.skip_login === "true";

    const locale = useCurrentLocale();
    const GIFT_CARD_ERROR_MESSAGES = {
        OUT_OF_STOCK: t("messages.outOfStock"),
        INVALID_RECIPIENT_EMAIL: t("messages.invalidEmail"),
        CANCELLED: t("messages.cancelledOrder"),
        PROMO_FAILED: t("messages.promoFailed"),
    };
    /*
     * When using Safari (desktop or iOS) the Afterpay SDK redirects the user to portal.afterpay.com instead of
     * opening a popup and on afterpay.com.
     * Once portal.afterpay.com redirects the user back to the gift card page it will add status and orderToken
     * query parameters the the URL.
     * The state and context are reset, but the status and orderToken query parameters let us resume the checkout.
     * Reading the checkout data from session storage allows us to continue the checkout process after the user is
     * redirected back to the gift card page.
     * */
    useEffect(() => {
        const paramStatus = getParam("status", window.location.href);
        const paramOrderToken = getParam("orderToken", window.location.href);

        setParamStatus(paramStatus);
        setParamToken(paramOrderToken);
    }, [locale]);

    useEffect(() => {
        if (!paramStatus || !paramToken) {
            return;
        }

        if (paramStatus === SDK_STATUSES.CANCELLED) {
            createErrorMessage(GIFT_CARD_ERROR_MESSAGES.CANCELLED);
            return;
        }
        let checkoutData: GiftCardCheckout;
        try {
            checkoutData = JSON.parse(sessionStorage.getItem(CHECKOUT_FIELDS));
        } catch {
            checkoutData = null;
        }
        if (checkoutData) {
            if (checkoutData.consumer) {
                setConsumer({
                    ...checkoutData.consumer,
                    confirmEmail: checkoutData.consumer.email,
                });
            }

            if (checkoutData.recipient) {
                setRecipient({
                    ...checkoutData.recipient,
                    confirmEmail: checkoutData.recipient.email,
                });
            }
            processOrder(paramToken, transformCheckout(checkoutData), locale)
                .then((orderId) => setOrderId(orderId))
                .catch((err) =>
                    createErrorMessage(
                        isPromotionFailure(err)
                            ? GIFT_CARD_ERROR_MESSAGES.PROMO_FAILED
                            : GIFT_CARD_ERROR_MESSAGES.CANCELLED
                    )
                );
        }
    }, [paramStatus, paramToken, locale]); // eslint-disable-line
    // react-hooks/exhaustive-deps

    useEffect(() => {
        if (orderId === null) {
            return;
        }
        const startTime = Date.now();
        const scheduleNextPoll = () =>
            new Promise((resolve) => setTimeout(resolve, GC_ORDER_STATUS_POLLING_INTERVAL)).then(
                pollOrderStatus
            );

        // In the in-app webview, once we've been polling past the delayed-notice
        // deadline the order has NOT failed — the supplier is just slow (or its
        // /status endpoint is timing out / erroring) — so stop polling and show a
        // terminal "still processing / don't re-order" notice instead of holding
        // the customer on an indefinite spinner. Scoped to the webview (skip_login)
        // flow; the web flow keeps polling to a definitive status as before.
        const shouldShowDelayedNotice = () =>
            shouldSkipLogin && Date.now() - startTime >= GC_ORDER_STATUS_DELAYED_NOTICE_TIMEOUT;

        const showDelayedNotice = (lastKnownStatus: OrderStatus | null) => {
            setOrderStatus({
                ...lastKnownStatus,
                orderStatus: ORDER_STATUSES.FULFILLMENT_DELAYED,
            });
            setIsPurchaseFlowComplete(true);
            setShowDelayedOverlay(true);
        };

        const pollOrderStatus = async () => {
            try {
                const orderStatus = await getOrderStatus(orderId, locale);
                setOrderStatus(orderStatus);

                if (POLLING_STATUS_CODES.includes(orderStatus.orderStatus)) {
                    if (shouldShowDelayedNotice()) {
                        showDelayedNotice(orderStatus);
                        return;
                    }

                    return scheduleNextPoll();
                }
            } catch (err) {
                // Only surface an error to the customer when the service reports a
                // legitimate, terminal order failure. A transport/network blip must
                // NOT error the flow — the order may well have succeeded, and showing
                // a false failure causes customers to place duplicate orders. Keep
                // polling until we get a definitive answer.
                if (!(err instanceof OrderFulfillmentError)) {
                    // …but don't retry transient errors forever: if the supplier's
                    // /status has been unreachable past the delayed-notice deadline,
                    // fall through to the same terminal "still processing" notice so
                    // the webview isn't left pending indefinitely.
                    if (shouldShowDelayedNotice()) {
                        showDelayedNotice(orderStatus);
                        return;
                    }

                    return scheduleNextPoll();
                }

                // Mark order status as error
                setOrderStatus({
                    ...orderStatus,
                    orderStatus: ORDER_STATUSES.FULFILLMENT_ERROR,
                    errorCode: err.errorCode,
                });
                setIsPurchaseFlowComplete(true);

                // The offer couldn't be applied (unavailable / already claimed) so the
                // service cancelled the order. This isn't a "try again" failure — tell
                // the customer why and that they can re-order at the regular price.
                if (err.isPromotionError) {
                    if (shouldSkipLogin) {
                        setPromoFailedReason(err.errorCode as PROMOTION_ERROR_CODE);
                    } else {
                        createErrorMessage(GIFT_CARD_ERROR_MESSAGES.PROMO_FAILED);
                    }
                    return;
                }

                if (shouldSkipLogin) {
                    setShowErrorOverlay(true);
                } else {
                    createErrorMessage(GIFT_CARD_ERROR_MESSAGES.CANCELLED);
                }
            }
        };

        setTimeout(() => {
            pollOrderStatus();
        }, INITIAL_GC_ORDER_STATUS_POLL_DELAY);
    }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!orderStatus) {
            return;
        }
        if (orderStatus.orderStatus === ORDER_STATUSES.FULFILLMENT_ERROR) {
            setShowConfirmation(false);
            return;
        }
        if (orderStatus.errorCode !== null) {
            setShowConfirmation(false);
            return;
        }
        // NBI does not have an order confirmation number. It only has a transaction id
        if (orderStatus.orderNumber === null && giftCard.supplier !== "NBI") {
            setShowConfirmation(false);
            return;
        }
        // Only confirm on an explicit success status; anything else (still polling, or a
        // malformed response) must not show the confirmation or clear the checkout data.
        if (!SUCCESS_STATUS_CODES.includes(orderStatus.orderStatus)) {
            setShowConfirmation(false);
            return;
        }
        setShowConfirmation(true);
    }, [
        giftCard,
        giftCard?.supplier,
        orderStatus,
        orderStatus?.orderNumber,
        orderStatus?.orderStatus,
        orderStatus?.errorCode,
    ]);

    if (promoFailedReason) {
        postToApp(WEBVIEW_MESSAGE_TYPES.SHOW_CLOSE_BUTTON);

        return (
            <GiftCardPromoFailed
                sku={giftCard.sku}
                isGift={router.query.is_gift === "true"}
                reason={promoFailedReason}
            />
        );
    }

    if (showErrorOverlay) {
        postToApp(WEBVIEW_MESSAGE_TYPES.SHOW_CLOSE_BUTTON);

        return (
            <InAppNotificationShell>
                <GiftCardCheckoutNotification
                    icon={<AlertCircleIconArcade />}
                    headerText={t("giftCards.errorOverlay.header")}
                    messageText={t("giftCards.errorOverlay.message")}
                    isError
                />
            </InAppNotificationShell>
        );
    }

    if (showDelayedOverlay) {
        postToApp(WEBVIEW_MESSAGE_TYPES.SHOW_CLOSE_BUTTON);

        return (
            <GiftCardOrderDelayed
                imageUrl={window.apAppGiftCardImageUrl ?? giftCard.imageUrl}
                sku={giftCard.sku}
                isGift={router.query.is_gift === "true"}
            />
        );
    }

    const showHeroOnlyVariant = [
        "WALMART",
        "SAM_S_CLUB_USD",
        "MACYS",
        "MACY_S_EGIFT_P1X0G4_BHN",
        "WALMART_EGIFT_B7K4GH_BHN",
        "MACY_S_EGIFT_93L9LD_BHN",
    ].includes(giftCard?.sku || giftCard?.code);

    return (
        <>
            {showConfirmation ? (
                <PaymentConfirmationLayout
                    paymentId={orderStatus.paymentId}
                    orderNumber={orderStatus.orderNumber}
                    email={recipient.email ? recipient.email : consumer.email}
                    sku={giftCard.sku}
                    variant={shouldSkipLogin ? "short" : "default"}
                    giftCardImageUrl={giftCard.imageUrl}
                    supplier={giftCard.supplier}
                    isGift={router.query.is_gift === "true"}
                />
            ) : (
                <>
                    <GiftCardDetailsLayout
                        giftCard={giftCard}
                        consumer={consumer}
                        recipient={recipient}
                        setShowErrorOverlay={setShowErrorOverlay}
                        setPromoFailedReason={setPromoFailedReason}
                        setConsumer={setConsumer}
                        setRecipient={setRecipient}
                        pollingStatus={orderStatus?.orderStatus}
                        setOrderId={setOrderId}
                        isPurchaseFlowComplete={isPurchaseFlowComplete}
                        setPurchaseFlowComplete={setIsPurchaseFlowComplete}
                        showHeroOnlyVariant={showHeroOnlyVariant}
                    />

                    {!showHeroOnlyVariant && (
                        <SplitterNumbers
                            title={t("giftCards.howItWorks.title")}
                            subtitle=""
                            accentColor={ACCENT_COLORS.ORCA_PASTEL}
                            fullWidth={false}
                            sections={howItWorksSections}
                            showNumbers={true}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default GiftCardLayout;
